import { useEffect, useState } from "react";
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  EyeOff,
  Cpu,
  Fingerprint,
  Ghost,
  Star,
  KeyRound,
  Users,
  Usb,
  CircleDollarSign,
  ServerCog,
  Ban,
  Play,
  X,
  MessageSquare,
  PhoneOff,
  Apple,
  Smartphone,
  Fingerprint as FingerprintIcon,
} from "lucide-react";
import {
  LOGO_URL,
  FEATURES,
  REVIEW_STATS,
  REVIEW_SOURCES,
  REVIEW_CARDS,
  VIDEO_REVIEWS,
  HERO_IMAGES,
} from "../mock";

const Home = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
    <main className="bg-black text-white">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 aurora-purple pointer-events-none" />
        <div className="absolute inset-0 grid-mask pointer-events-none" />

        <div className="relative z-10 text-center px-6 max-w-5xl reveal-fade">
          <img
            src={LOGO_URL}
            alt="Phantom Industries"
            className="h-64 md:h-80 w-auto mx-auto object-contain"
            style={{
              filter: "drop-shadow(0 0 80px rgba(168,85,247,0.55))",
            }}
          />
          <div className="mt-6 chip mx-auto">
            <ShieldCheck className="h-3 w-3" /> Phantom Industries · Privacy by design
          </div>
          <h1 className="mt-6 font-headline text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95]">
            <span className="text-gradient-purple">Your privacy,</span>
            <br />
            <span className="text-gradient-phantom">without a trace.</span>
          </h1>
          <p className="mt-6 text-sm md:text-base uppercase tracking-[0.35em] text-purple-300/90 font-semibold">
            Privacy Perfected. Phantom Protected.
          </p>
          <p className="mt-4 text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
            We build the hardware and software that leave no breadcrumbs.
            Encrypted phones. Anonymous messaging. Every product engineered from
            the metal up to make you invisible.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/phones" className="btn-primary">
              Explore Phantom Phone <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/chat" className="btn-ghost">
              Explore Phantom X <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Marquee */}
        <div className="absolute bottom-8 left-0 right-0 overflow-hidden opacity-60">
          <div className="marquee-track whitespace-nowrap text-xs uppercase tracking-[0.35em] text-white/40">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12 items-center">
                <span>End-to-end encryption</span><span>•</span>
                <span>Zero-knowledge architecture</span><span>•</span>
                <span>No tracking. No telemetry.</span><span>•</span>
                <span>Hardware-gated access</span><span>•</span>
                <span>Anonymous by design</span><span>•</span>
                <span>Australian owned</span><span>•</span>
                <span>Ships worldwide</span><span>•</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO PRODUCT FAMILIES — Phantom Industries hub */}
      <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="product-families">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="chip mx-auto">
              <Ghost className="h-3 w-3" /> A family of privacy tools
            </div>
            <h2 className="mt-5 font-headline text-4xl md:text-5xl font-bold text-gradient-purple leading-tight">
              Two products. One promise.
            </h2>
            <p className="mt-4 text-white/60">
              Phantom Industries builds hardware and software that leave no
              breadcrumbs. Pick your entry point.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {/* Phantom Phone */}
            <Link
              to="/phones"
              className="group card-glass rounded-3xl p-8 border border-white/10 hover:border-purple-500/50 transition-colors relative overflow-hidden"
              data-testid="family-phantom-phone"
            >
              <div className="absolute inset-0 aurora-purple opacity-30 group-hover:opacity-60 transition-opacity pointer-events-none" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] text-purple-300">Hardware</div>
                <div className="mt-6 h-40 flex items-center justify-center rounded-2xl bg-black/60 border border-white/10 p-4">
                  <img
                    src="/img/phantom-phone-logo.png"
                    alt="Phantom Phone"
                    className="h-full object-contain drop-shadow-[0_0_30px_rgba(168,85,247,0.35)]"
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 font-headline text-3xl font-bold text-white">
                  Phantom Phone
                </div>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  Genuine Google Pixels, wiped and re-flashed with hardened
                  GrapheneOS at one of three privacy tiers. From $1,049.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-purple-300 font-medium group-hover:text-white transition-colors">
                  Shop encrypted phones <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>

            {/* Phantom X */}
            <Link
              to="/chat"
              className="group card-glass rounded-3xl p-8 border border-white/10 hover:border-purple-500/50 transition-colors relative overflow-hidden"
              data-testid="family-phantom-chat"
            >
              <div className="absolute inset-0 aurora-purple opacity-30 group-hover:opacity-60 transition-opacity pointer-events-none" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] text-purple-300">Software · Coming soon</div>
                <div className="mt-6 h-40 rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="/img/phantom-key.jpg"
                    alt="Phantom X — anonymous encrypted messenger"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 font-headline text-3xl font-bold text-white">
                  Phantom X
                </div>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  Encrypted messenger for iOS + Android. No phone, no email,
                  no account — only works while a Phantom Key is plugged in.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-purple-300 font-medium group-hover:text-white transition-colors">
                  Join the waitlist <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>


      {/* VIDEO TESTIMONIALS — "video thumbnail" grid */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="chip mx-auto">
              <Play className="h-3 w-3 fill-purple-300 text-purple-300" /> Customer stories
            </div>
            <h2 className="mt-5 font-headline text-4xl md:text-6xl font-bold text-gradient-purple leading-[0.98]">
              Hear it from them.
            </h2>
            <p className="mt-4 text-white/60">
              Real Australians who trust Phantom Industries with their most private data.
              Tap any card to read their full story.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {VIDEO_REVIEWS.map((v) => (
              <button
                key={v.name}
                onClick={() => setActiveVideo(v)}
                className="group relative rounded-2xl overflow-hidden card-glass hover-lift text-left focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <div className="aspect-[9/16] relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-700"
                    style={{ backgroundImage: `url(${v.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-purple-500/90 backdrop-blur-sm flex items-center justify-center shadow-[0_0_40px_-4px_rgba(168,85,247,0.7)] group-hover:scale-110 transition-transform">
                      <Play className="h-5 w-5 text-black fill-black ml-0.5" />
                    </div>
                  </div>

                  {/* Star rating badge */}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 border border-white/10">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="h-2.5 w-2.5 fill-purple-300 text-purple-300" />
                    ))}
                  </div>

                  {/* Overlay caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-purple-300 mb-1">
                      {v.name}
                    </div>
                    <div className="text-sm font-display font-semibold text-white leading-tight line-clamp-2">
                      {v.hook}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>



      {/* FEATURES */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="chip">
                <Fingerprint className="h-3 w-3" /> Built to disappear
              </div>
              <h2 className="mt-5 font-headline text-4xl md:text-5xl font-bold text-gradient-purple">
                Privacy isn't a setting. It's the architecture.
              </h2>
              <p className="mt-4 text-white/60">
                Every product Phantom Industries ships is engineered to leave
                nothing behind — no telemetry, no accounts, no metadata trail.
                Security isn't a checkbox we tick; it is the whole design brief.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {FEATURES.map((f) => (
                <div key={f.title} className="card-glass rounded-2xl p-6 hover-lift">
                  <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mb-4">
                    <ShieldCheck className="h-4 w-4 text-purple-300" />
                  </div>
                  <div className="font-display font-semibold text-white">{f.title}</div>
                  <div className="mt-2 text-sm text-white/60 leading-relaxed">{f.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS — sources + verified customer cards */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="chip mx-auto">
              <Star className="h-3 w-3 fill-purple-300 text-purple-300" /> Field-verified reviews
            </div>
            <h2 className="mt-5 font-headline text-4xl md:text-6xl font-bold text-gradient-purple leading-[0.98]">
              {REVIEW_STATS.total.toLocaleString()}+ verified customer voices.
            </h2>
            <p className="mt-4 text-white/60">
              Our customers can't leave named reviews without breaking their own privacy —
              so we do it their way: PGP-signed messages and encrypted DM conversations we can point to.
            </p>
            <div className="mt-6 inline-flex items-center gap-2">
              <div className="flex items-center gap-1">
                {[0,1,2,3,4].map((i) => (
                  <Star key={i} className="h-5 w-5 fill-purple-300 text-purple-300" />
                ))}
              </div>
              <span className="font-display text-2xl font-bold text-white">{REVIEW_STATS.average}</span>
              <span className="text-sm text-white/50">/ 5</span>
              <span className="mx-2 h-4 w-px bg-white/20" />
              <span className="text-sm text-white/60">{REVIEW_STATS.five_star_pct}% five-star</span>
            </div>
          </div>

          {/* Source cards */}
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {REVIEW_SOURCES.map((s) => {
              const Ico = s.id === "pgp" ? KeyRound : s.id === "telegram" ? Lock : Users;
              return (
                <div key={s.id} className="card-glass rounded-3xl p-7 hover-lift">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                      <Ico className="h-5 w-5 text-purple-300" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-white">{s.name}</div>
                      <div className="text-xs text-white/50 mt-0.5">{s.tagline}</div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="flex items-center gap-0.5">
                      {[0,1,2,3,4].map((i) => (
                        <Star key={i} className="h-4 w-4 fill-purple-300 text-purple-300" />
                      ))}
                    </div>
                    <div className="font-display text-lg font-bold text-white">{s.rating}</div>
                    <div className="text-xs text-white/50">· {s.count} reviews</div>
                  </div>
                  <blockquote className="mt-5 text-sm text-white/75 leading-relaxed italic">
                    {s.featured}
                  </blockquote>
                  <div className="mt-6 text-xs text-purple-300 hover:text-white inline-flex items-center gap-1 cursor-pointer">
                    Read all → <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent verified reviews */}
          <div className="mt-16 flex items-end justify-between mb-6">
            <h3 className="font-headline text-2xl md:text-3xl font-bold text-white">Most recent reviews</h3>
            <div className="text-sm text-purple-300">See all reviews →</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {REVIEW_CARDS.map((r, i) => (
              <div key={i} className="card-glass rounded-2xl p-5 hover-lift flex flex-col">
                <div className="flex items-center gap-1 text-purple-300">
                  {[0,1,2,3,4].map((s) => (
                    <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-purple-300 text-purple-300" : "text-white/20"}`} />
                  ))}
                </div>
                <p className="mt-3 text-sm text-white/80 leading-relaxed flex-1">"{r.text}"</p>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-xs font-display font-semibold text-purple-200">
                    {r.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-white/80 truncate">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 truncate">
                      {r.role}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.15em]">
                  <span className="text-purple-300 inline-flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> {r.verified}-verified
                  </span>
                  <span className="text-white/40">{r.when}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden border-t border-white/5">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES.hooded})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <div className="chip mx-auto">Ready when you are</div>
          <h2 className="mt-5 font-headline text-5xl md:text-7xl font-bold text-gradient-phantom">
            Choose your Phantom.
          </h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            Pick the product family that fits your threat model. Both leave no
            trace. Both are built by the same small Australian team.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/phones" className="btn-primary">
              Explore Phantom Phone <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/chat" className="btn-ghost">
              Explore Phantom X <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
    {activeVideo && (
      <VideoReviewModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    )}
    </>
  );
};

export default Home;

const Step = ({ icon: Icon, n, title, body }) => (
  <div className="flex gap-4 items-start">
    <div className="relative h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
      <Icon className="h-5 w-5 text-purple-300" />
      <div className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-purple-500 text-black text-[10px] font-bold flex items-center justify-center">
        {n}
      </div>
    </div>
    <div className="pt-0.5">
      <div className="font-display font-semibold text-white">{title}</div>
      <div className="mt-1 text-sm text-white/60 leading-relaxed">{body}</div>
    </div>
  </div>
);

const VideoReviewModal = ({ video, onClose }) => (
  <div
    className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
    onClick={onClose}
  >
    <div
      className="relative bg-[#0a0a0a] border border-white/10 rounded-3xl w-full max-w-4xl overflow-hidden my-8"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-black/60 border border-white/10 text-white/70 hover:text-white flex items-center justify-center"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="grid md:grid-cols-[380px_1fr]">
        <div className="relative aspect-[9/16] md:aspect-auto md:min-h-[520px]">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${video.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300">
              {video.name}
            </div>
            <div className="text-xs text-white/70 mt-0.5">{video.role}</div>
          </div>
        </div>
        <div className="p-8 md:p-10 flex flex-col">
          <div className="flex items-center gap-1 text-purple-300">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star
                key={s}
                className={`h-4 w-4 ${
                  s < video.rating ? "fill-purple-300 text-purple-300" : "text-white/20"
                }`}
              />
            ))}
          </div>
          <div className="mt-4 font-headline text-2xl md:text-3xl font-bold text-gradient-purple leading-[1.05]">
            {video.hook}
          </div>
          <p className="mt-6 text-white/80 leading-relaxed">{video.quote}</p>
          <div className="mt-auto pt-8 flex items-center justify-between border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/40">Verified customer</div>
              <div className="text-sm text-white/80 mt-0.5">{video.name} · {video.role}</div>
            </div>
            <span className="text-xs text-purple-300 inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> PGP-verified
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);
